import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { TimeSignal } from 'graphql/types';
import {
  NOTES_GROUP_BY_TYPE,
  useNotesFilterContext
} from 'modules/notes/NotesFilterContextProvider';
import { useMeContext } from 'modules/me';
import { NotesGroupBySorter } from './NotesGroupBySorter';
import { LabelAndTimeGroupByStrategy } from './LabelAndTimeGroupByStrategy';
import { NoOpGroupByStrategy } from './NoOpGroupByStrategy';
import { LabelGroupByStrategy } from './LabelGroupByStrategy';

export const useRegisterLabelGroupsWithForm = (
  groupedNotes: Record<string, TimeSignal[]>
): void => {
  const { register, setValue, getValues } = useFormContext();
  const labelGroupIds = useMemo(() => {
    return Object.keys(groupedNotes);
  }, [groupedNotes]);

  useEffect(() => {
    labelGroupIds.forEach((key) => {
      register(key);
      const values = getValues();
      setValue(key, values[key]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelGroupIds]);
};

export const useNotesGroupBy = (
  notes: TimeSignal[],
  day: string
): Record<string, TimeSignal[]> => {
  const {
    filterContext: { groupBy }
  } = useNotesFilterContext();
  const me = useMeContext();
  const isStartEndFieldsVisible = me?.showStartEndFields;

  const groupedNotes = useMemo(() => {
    if (notes.length === 0) {
      return {};
    }

    const strategy =
      groupBy === NOTES_GROUP_BY_TYPE.LABELS && isStartEndFieldsVisible
        ? new LabelAndTimeGroupByStrategy(day)
        : groupBy === NOTES_GROUP_BY_TYPE.LABELS && !isStartEndFieldsVisible
        ? new LabelGroupByStrategy(day)
        : new NoOpGroupByStrategy();

    const sorter = new NotesGroupBySorter(strategy);
    return sorter.generateGroups(notes);
  }, [day, groupBy, isStartEndFieldsVisible, notes]);
  useRegisterLabelGroupsWithForm(groupedNotes);
  return groupedNotes;
};
